import { useCallback, useEffect, useRef, useState } from 'react';
import { api, type CachedSpool } from '../lib/api.js';
import { formatDate } from '../lib/front.js';
import { Button } from './ui.js';

interface Props {
  items: CachedSpool[];
  canAct: boolean;
  busyId: string | null;
  onRetrieve: (spool: CachedSpool) => void;
  onAuthorize: (spool: CachedSpool) => void;
}

/** Delai avant ouverture au survol : evite que la fiche clignote au passage de la souris. */
const HOVER_DELAY_MS = 350;

/** Champs mis en avant en tete de fiche : les trois qu'on lit vraiment. */
const CHAMPS_EN_TETE = new Set(['subject', 'senderEnvelope', 'recipientEnvelope']);

/** Champs sans interet a l'ecran : redondants, internes, ou toujours vides. */
const CHAMPS_MASQUES = new Set(['id', 'clientId', 'userId', 'flag', 'labelString']);

/** Libelles francais des champs renvoyes par l'API. */
const LIBELLES: Record<string, string> = {
  createDate: 'Reçu le',
  subject: 'Objet',
  senderEnvelope: 'Expéditeur (enveloppe)',
  senderUsed: 'Expéditeur affiché',
  recipientEnvelope: 'Destinataire',
  smtpClientIp: 'IP émettrice',
  postfixIn: 'Identifiant entrant',
  postfixOut: 'Identifiant sortant',
  deliveryStatus: 'Statut de remise',
  type: 'Type',
  store: 'Classement',
  text: 'Contenu',
  isNewsletter: 'Newsletter',
  inProcess: 'En traitement',
  read: 'Lu',
  history: 'Historique',
  label: 'Analyse',
  actionLock: 'Action verrouillée',
};

function formatValue(key: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'oui' : 'non';

  // Les dates arrivent en tableau d'entiers, pas en chaine ISO.
  if (key.toLowerCase().includes('date') && Array.isArray(value)) {
    const [y, m, d, h = 0, min = 0] = value as number[];
    if (y && m && d) {
      return new Date(Date.UTC(y, m - 1, d, h, min)).toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
  }

  if (typeof value === 'object') {
    const json = JSON.stringify(value);
    return json === '{}' || json === '[]' ? '—' : json;
  }
  return String(value);
}

/**
 * Fiche complete d'un message : tout ce que l'API renvoie.
 *
 * Le corps du message n'en fait pas partie — le champ `text` de `GET /spools`
 * est systematiquement nul et aucun endpoint ne permet de le recuperer. La
 * fiche montre donc les metadonnees reelles, verdict d'analyse compris.
 */
function Fiche({ id }: { id: string }) {
  const [raw, setRaw] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .detail(id)
      .then((result) => {
        if (!cancelled) setRaw(result.raw);
      })
      .catch((cause: Error) => {
        if (!cancelled) setError(cause.message);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (error) return <div className="fiche fiche-vide">{error}</div>;
  if (!raw) return <div className="fiche fiche-vide">Chargement…</div>;

  // Objet, expediteur et destinataire sont mis en avant : ce sont les trois
  // informations qu'on lit vraiment. Le reste vient apres, en plus petit.
  const secondaires = Object.entries(raw).filter(
    ([key]) => !CHAMPS_MASQUES.has(key) && !CHAMPS_EN_TETE.has(key),
  );

  return (
    <div className="fiche">
      <p className="fiche-objet">{String(raw.subject ?? '') || '(sans objet)'}</p>

      <dl className="fiche-cles">
        <div className="fiche-cle">
          <dt>De</dt>
          <dd>{formatValue('senderEnvelope', raw.senderEnvelope)}</dd>
        </div>
        <div className="fiche-cle">
          <dt>À</dt>
          <dd>{formatValue('recipientEnvelope', raw.recipientEnvelope)}</dd>
        </div>
      </dl>

      <dl className="fiche-reste">
        {secondaires.map(([key, value]) => (
          <div key={key} className="fiche-ligne">
            <dt>{LIBELLES[key] ?? key}</dt>
            <dd>{formatValue(key, value)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export function SpoolList({ items, canAct, busyId, onRetrieve, onAuthorize }: Props) {
  // `pinned` survit au depart de la souris (clic), `hovered` non.
  const [pinned, setPinned] = useState<string | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  const timer = useRef<number | null>(null);

  const cancelTimer = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  useEffect(() => cancelTimer, [cancelTimer]);

  return (
    <ul className="spool-list">
      {items.map((spool, index) => {
        const open = pinned === spool.id || hovered === spool.id;
        return (
          <li
            key={spool.id}
            className={`spool spool-${spool.category}${index % 2 === 1 ? ' spool-alt' : ''}${
              open ? ' spool-open' : ''
            }`}
            onMouseEnter={() => {
              cancelTimer();
              timer.current = window.setTimeout(() => setHovered(spool.id), HOVER_DELAY_MS);
            }}
            onMouseLeave={() => {
              cancelTimer();
              setHovered((current) => (current === spool.id ? null : current));
            }}
          >
            {/* Ligne 1 : date, objet, épingle. */}
            <div className="spool-line">
              <span className="spool-date">{formatDate(spool.createdAt)}</span>
              <span className="spool-subject">{spool.subject || '(sans objet)'}</span>
              <button
                type="button"
                className={`spool-pin${pinned === spool.id ? ' spool-pin-on' : ''}`}
                aria-pressed={pinned === spool.id}
                title={pinned === spool.id ? 'Refermer la fiche' : 'Garder la fiche ouverte'}
                onClick={() => setPinned(pinned === spool.id ? null : spool.id)}
              >
                {pinned === spool.id ? '▾' : '▸'}
              </button>
            </div>

            {/* Ligne 2 : expéditeur et actions, côte à côte pour tenir la hauteur. */}
            <div className="spool-line spool-line-second">
              <span className="spool-sender" title={spool.sender}>
                {spool.sender || '—'}
              </span>
              {canAct && (
                <span className="spool-actions">
                  <Button
                    small
                    disabled={busyId !== null}
                    title="Délivrer ce message dans votre boîte, sans modifier les listes"
                    onClick={() => onRetrieve(spool)}
                  >
                    {busyId === spool.id ? '…' : 'Délivrer'}
                  </Button>
                  <Button
                    small
                    variant="primary"
                    disabled={busyId !== null}
                    title="Autoriser durablement cet expéditeur et délivrer le message"
                    onClick={() => onAuthorize(spool)}
                  >
                    Autoriser
                  </Button>
                </span>
              )}
            </div>

            {open && <Fiche id={spool.id} />}
          </li>
        );
      })}
    </ul>
  );
}
